import {
  defaultStatementEn,
  defaultStatementSv,
  FormValues,
  StepBoardStatement,
} from '../../../../common/components/StepBoardStatement';
import { WarrantProgramDocument } from '../../../WarrantProgram.graphql';
import {
  GqlBoardsStatementOption,
  GqlCreateIdentityInput,
  GqlMeeting,
  GqlMeetingConcern,
  GqlMeetingKind,
  GqlSnrAuthorization,
  GqlWarrantBoardMember,
  GqlWarrantProgram,
  GqlWarrantProgramKind,
} from '../../../../../graphql/base-types.graphql';
import { DisplayIdentity } from '@startuptools/business-ui/identity/Identity';
import {
  useCreateWarrantBoardMemberMutation,
  useDeleteWarrantBoardMemberMutation,
  useWpSetBoardsStatementMutation,
} from './StepBoardStatement.graphql';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';
import { SubmitHandler, useForm } from 'react-hook-form';
import { DateTime } from 'luxon';
import { isNil } from 'lodash-es';
import { Trans } from '@lingui/react';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { wpEditDocumentsLink, wpEditOptionsSubsLink } from '../../../../../components/Links';
import { useNavigate } from '../../../../../components/Navigate';

export const WpStepBoardStatement = ({
  warrantProgram,
}: {
  warrantProgram: Pick<
    GqlWarrantProgram,
    | 'id'
    | 'companyId'
    | 'secondaryLang'
    | 'boardsStatementOption'
    | 'boardsStatementDate'
    | 'boardsStatementSv'
    | 'boardsStatementEn'
    | 'warrantStartDate'
    | 'usesStepper'
    | 'warrantKind'
  > & {
    warrantBoardMembers: (Pick<GqlWarrantBoardMember, 'id'> & { identity: DisplayIdentity })[];
    meetingConcern?:
      | (Pick<GqlMeetingConcern, 'id'> & { meeting: Pick<GqlMeeting, 'id' | 'startTime' | 'kind'> })
      | null;
    snrAuthorization?: Pick<GqlSnrAuthorization, 'id' | 'noBoardStatement'> | null;
  };
}) => {
  const { stepNext } = useStepper();
  const { navigate } = useNavigate();
  // eslint-disable-next-line sut/loading-mutation
  const [doCreateWbm] = useCreateWarrantBoardMemberMutation({ refetchQueries: [WarrantProgramDocument] });
  // eslint-disable-next-line sut/loading-mutation
  const [doDeleteWbm] = useDeleteWarrantBoardMemberMutation({ refetchQueries: [WarrantProgramDocument] });
  const [doSetBoardsStatement, { loading }] = useWpSetBoardsStatementMutation({
    refetchQueries: [WarrantProgramDocument],
    awaitRefetchQueries: true,
  });

  const handleCreateBoardMember = async (identity: GqlCreateIdentityInput) => {
    return doCreateWbm({
      variables: {
        companyId: warrantProgram.companyId,
        input: { identityInput: identityToInput(identity), warrantProgramId: warrantProgram.id },
      },
    });
  };

  const { control, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: {
      boardsStatementOption: warrantProgram.boardsStatementOption,
      boardsStatementDate: warrantProgram.boardsStatementDate
        ? DateTime.fromISO(warrantProgram.boardsStatementDate)
        : warrantProgram.meetingConcern?.meeting
          ? DateTime.fromISO(warrantProgram.meetingConcern.meeting.startTime).startOf('day')
          : warrantProgram.warrantStartDate
            ? DateTime.fromISO(warrantProgram.warrantStartDate)
            : DateTime.now(),
      boardsStatementSv: warrantProgram.boardsStatementSv?.replaceAll('&nbsp;', ' ') || defaultStatementSv,
      boardsStatementEn: warrantProgram.boardsStatementEn?.replaceAll('&nbsp;', ' ') || defaultStatementEn,
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async ({ boardsStatementDate, ...values }) => {
    if (!values.boardsStatementOption) {
      return;
    }
    await doSetBoardsStatement({
      variables: {
        companyId: warrantProgram.companyId,
        input: {
          id: warrantProgram.id,
          boardsStatementDate: boardsStatementDate?.toUTC(0, { keepLocalTime: true }).toString() ?? null,
          boardsStatementOption: values.boardsStatementOption,
          boardsStatementSv: values.boardsStatementSv,
          boardsStatementEn: values.boardsStatementEn,
        },
      },
    });
    return stepNext(() => {
      const link =
        warrantProgram.warrantKind === GqlWarrantProgramKind.Warrant
          ? wpEditOptionsSubsLink(warrantProgram)
          : wpEditDocumentsLink(warrantProgram);
      return navigate(link);
    });
  };

  const currentBoardsStatementOption = watch('boardsStatementOption');

  return (
    <StepBoardStatement
      control={control}
      handleSubmit={handleSubmit}
      onSubmit={onSubmit}
      loading={loading}
      secondaryLang={warrantProgram.secondaryLang}
      boardMemberIdentities={warrantProgram.warrantBoardMembers}
      noBoardsStatement={warrantProgram.snrAuthorization?.noBoardStatement ?? false}
      showNone={warrantProgram.meetingConcern?.meeting.kind === GqlMeetingKind.ExtraShareholdersMeeting}
      showWaivedByShareholders={
        warrantProgram.meetingConcern?.meeting.kind === GqlMeetingKind.ExtraShareholdersMeeting &&
        warrantProgram?.boardsStatementOption === GqlBoardsStatementOption.Waiver
      }
      showWaivedByAuth={
        warrantProgram.boardsStatementOption === GqlBoardsStatementOption.WaivedByAuth ||
        (warrantProgram.meetingConcern?.meeting.kind === GqlMeetingKind.BoardMeeting &&
          !isNil(warrantProgram.snrAuthorization))
      }
      waiverLabel={<Trans id="Ask the shareholders to accept that no statement according to Chapter 14 § 8 is made" />}
      handleCreateBoardMember={handleCreateBoardMember}
      handleDeleteBoardMember={id => doDeleteWbm({ variables: { companyId: warrantProgram.companyId, id: id } })}
      currentBoardsStateOptions={currentBoardsStatementOption ?? GqlBoardsStatementOption.None}
    />
  );
};
